import { useMemo } from "react";
import { useDebouncedValue } from "@/common/hooks/use-debounced-value";
import { useCities, useCountries, usePhoneCodes, useStates } from "@/common/hooks/use-locations";
import type { Country } from "@/services/locations/locations.types";

const CITIES_QUERY_DEBOUNCE_MS = 1_000;

export interface AddressLocationOptions {
  countries: Country[];
  phoneCodes: string[];
  matchedCountry: Country | undefined;
  states: string[] | undefined;
  cities: string[] | undefined;
  isStatesLoading: boolean;
  isCitiesLoading: boolean;
}

export const useAddressLocationOptions = (
  countryName: string,
  state: string
): AddressLocationOptions => {
  const { data: countries = [] } = useCountries();
  const { data: phoneCodes = [] } = usePhoneCodes();
  const matchedCountry = useMemo(
    () => countries.find((candidate) => candidate.name === countryName),
    [countries, countryName]
  );
  const statesQuery = useStates(matchedCountry?.iso2);
  const debouncedState = useDebouncedValue(state, CITIES_QUERY_DEBOUNCE_MS);
  const citiesQuery = useCities(matchedCountry?.iso2, debouncedState || undefined);
  const isCitiesDebouncePending = !!matchedCountry?.iso2 && !!state && state !== debouncedState;

  return {
    countries,
    phoneCodes,
    matchedCountry,
    states: statesQuery.data,
    cities: citiesQuery.data,
    isStatesLoading: statesQuery.isFetching,
    isCitiesLoading: citiesQuery.isFetching || isCitiesDebouncePending,
  };
};
